import { useParams, Link } from 'react-router-dom';
import { useContext, useState } from 'react';
import { SiteContentContext } from '../context/SiteContentContext';
import { CardsContext } from '../context/CardsContext';
import QuestionModal from '../components/QuestionModal';
import { useSeo, useJsonLd, toDescription, absUrl, BASE } from '../hooks/useSeo';
import '../styles/profile_page.css';

const TYPE_LABELS = { adventures: 'Путешествие', study: 'Обучение', races: 'Регата' };
const MONTHS_SHORT = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];

function nearestDate(dates) {
  const today = new Date().toISOString().slice(0, 10);
  const future = (dates || []).map(d => typeof d === 'object' ? d.isoDate : d).filter(d => d >= today).sort();
  if (!future.length) return null;
  const [y, m, day] = future[0].split('-');
  return `${Number(day)} ${MONTHS_SHORT[Number(m)-1]} ${y}`;
}

function RelatedCard({ card, to }) {
  const nearest = nearestDate(card.availableDates);
  const dur = card.durationDays > 1 ? `${card.durationDays} дн.` : null;
  const meta = [nearest && `📅 ${nearest}`, dur && `⏱ ${dur}`].filter(Boolean).join('  ·  ');
  return (
    <Link to={to} className="profile-card-link">
      {card.image && <div className="profile-card-img" style={{ backgroundImage: `url(${card.image})` }} />}
      <div className="profile-card-info">
        {card._type && <span className={`profile-card-badge profile-card-badge--${card._type}`}>{TYPE_LABELS[card._type]}</span>}
        <strong>{card.title}</strong>
        {card.price && <span className="profile-card-price">{card.price}</span>}
        {meta && <span className="profile-card-meta">{meta}</span>}
      </div>
    </Link>
  );
}

const InstructorPage = ({ isPreview = false }) => {
  const { instructorId } = useParams();
  const { content } = useContext(SiteContentContext);
  const { cards, productionCards } = useContext(CardsContext);
  const [isQuestionOpen, setIsQuestionOpen] = useState(false);
  const p = isPreview ? '/preview' : '';

  const source = isPreview ? cards : productionCards;
  const instructor = (content.instructors || []).find(i => i.slug === instructorId || String(i.id) === instructorId);

  useSeo(instructor ? {
    title: [instructor.name, instructor.title].filter(Boolean).join(', '),
    description: toDescription(instructor.bio) || `${instructor.name} — инструктор парусной школы The Sailing Doctor.`,
    image: instructor.photo,
  } : { title: 'Инструктор не найден' });

  useJsonLd(instructor ? {
    '@type': 'Person',
    name: instructor.name,
    jobTitle: instructor.title || 'Инструктор по яхтингу',
    description: toDescription(instructor.bio, 300) || undefined,
    image: instructor.photo ? absUrl(instructor.photo) : undefined,
    url: `${BASE}/instructor/${instructor.slug || instructor.id}`,
    worksFor: { '@type': 'Organization', name: 'The Sailing Doctor', url: BASE },
  } : null);

  if (!instructor) {
    return (
      <main className="profile-page">
        <div className="profile-not-found">
          <h2>Инструктор не найден</h2>
          <Link to={`${p}/team`} className="back-link">← Команда</Link>
        </div>
      </main>
    );
  }

  const allCards = [
    ...(source.adventures || []).map(c => ({ ...c, _type: 'adventures' })),
    ...(source.study || []).map(c => ({ ...c, _type: 'study' })),
    ...(source.races || []).map(c => ({ ...c, _type: 'races' })),
  ].filter(c => c.isPublished && (String(c.instructorId) === String(instructor.id) || c.captain === instructor.name));

  return (
    <main>
      <div className="profile-page">
      <div className="profile-hero profile-hero--instructor">
        {instructor.photo && (
          <div className="profile-hero-avatar" style={{ backgroundImage: `url(${instructor.photo})` }} />
        )}
        <div className="profile-hero-content">
          <div className="profile-badge">🧑‍✈️ Инструктор</div>
          <h1>{instructor.name}</h1>
          {instructor.title && <p className="profile-subtitle">{instructor.title}</p>}
        </div>
      </div>

      <div className="profile-body">
        <div className="profile-main">
          {instructor.bio && (
            <section className="profile-section">
              <h2>Об инструкторе</h2>
              <p>{instructor.bio}</p>
            </section>
          )}

          {allCards.length > 0 && (
            <section className="profile-section">
              <h2>Мероприятия с этим инструктором</h2>
              <div className="profile-cards">
                {allCards.map(card => {
                  const tp = { adventures: 'adventures', study: 'study', races: 'races', main: 'news' };
                  const path = tp[card.type] || 'card';
                  return <RelatedCard key={card.id} card={card} to={`${p}/${path}/${card.slug || card.id}`} />;
                })}
              </div>
            </section>
          )}
        </div>

        <aside className="profile-sidebar">
          <Link to={`${p}/team`} className="profile-back-link">← Команда</Link>
        </aside>
      </div>
      </div>
      <section className="cta-section">
        <h2>Остались вопросы?</h2>
        <p>Напишите нам — ответим в течение дня.</p>
        <button className="cta-button" onClick={() => setIsQuestionOpen(true)}>Задать вопрос</button>
      </section>
      <QuestionModal isOpen={isQuestionOpen} onClose={() => setIsQuestionOpen(false)} pageTitle="Инструктор" />
    </main>
  );
};

export default InstructorPage;
